import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Box,
    Slider,
    Button,
    Alert,
    Snackbar,
    Divider,
    Backdrop,
    TextField,
    Grid,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import BugReportIcon from '@mui/icons-material/BugReport';
import { useTranslation } from 'react-i18next';
import { getAnimationSpeed } from '../../config/animationConfig';
import { sendErrorReport } from '../../services/SentryService';
import gameLogger from '../../services/GameLogger';
import gameStateManager from '../../services/GameStateManager';

const GameSettingsInGameModal = ({
    open,
    onClose,
    isPaused,
    onPauseToggle,
    gameType,
    getGameState,
    currentPhase,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isLandscape = useMediaQuery('(orientation: landscape) and (max-height: 600px)');
    const [animationSpeed, setAnimationSpeed] = useState(1.0);
    const [sendingReport, setSendingReport] = useState(false);
    const [savingGame, setSavingGame] = useState(false);
    const [reportMessage, setReportMessage] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Load animation speed on mount
    useEffect(() => {
        const savedSpeed = getAnimationSpeed();
        setAnimationSpeed(savedSpeed);
    }, []);

    // Auto-pause when modal opens
    useEffect(() => {
        if (open && !isPaused && onPauseToggle) {
            onPauseToggle(true);
            gameLogger.log('modal_opened_auto_pause', { gameType });
        }
    }, [open]);

    // Handle animation speed change
    const handleAnimationSpeedChange = (event, newValue) => {
        setAnimationSpeed(newValue);
        localStorage.setItem('animationSpeed', newValue.toString());
        window.dispatchEvent(new CustomEvent('animationSpeedChanged', { detail: newValue }));
    };

    // Handle close - resume game
    const handleClose = () => {
        if (onPauseToggle) {
            onPauseToggle(false);
            gameLogger.log('modal_closed_resume', { gameType });
        }
        onClose();
    };

    // Handle save game
    const handleSaveGame = async () => {
        setSavingGame(true);
        try {
            const state = getGameState ? getGameState() : {};
            await gameStateManager.saveGameState(gameType, state);
            setSnackbar({
                open: true,
                message: 'Partida guardada correctamente',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error saving game:', error);
            setSnackbar({
                open: true,
                message: 'Error al guardar la partida',
                severity: 'error'
            });
        } finally {
            setSavingGame(false);
        }
    };

    // Handle send error report
    const handleSendErrorReport = async () => {
        setSendingReport(true);
        try {
            const gameState = getGameState ? getGameState() : {};
            const logs = gameLogger.getLogs();

            const success = await sendErrorReport({
                logs,
                gameState,
                currentScreen: `/${gameType}`,
                gamePhase: currentPhase || 'unknown',
                screenSize: {
                    width: window.innerWidth,
                    height: window.innerHeight,
                },
                userMessage: reportMessage,
            });

            if (success) {
                setSnackbar({
                    open: true,
                    message: 'Informe enviado correctamente. ¡Gracias por tu ayuda!',
                    severity: 'success'
                });
                setReportMessage(''); // Clear message after successful send
            } else {
                setSnackbar({
                    open: true,
                    message: 'Error al enviar el informe',
                    severity: 'error'
                });
            }
        } catch (error) {
            console.error('Error sending report:', error);
            setSnackbar({
                open: true,
                message: 'Error al enviar el informe',
                severity: 'error'
            });
        } finally {
            setSendingReport(false);
        }
    };

    // Speed marks
    const speedMarks = [
        { value: 0.2, label: 'Lento' },
        { value: 1.0, label: 'Normal' },
        { value: 3.0, label: 'Rápido' },
    ];

    return (
        <>
            {/* Backdrop with "Juego Pausado" text */}
            <Backdrop
                open={open}
                sx={{
                    zIndex: (theme) => theme.zIndex.modal - 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.85)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    pt: 10,
                }}
            >
                <Typography
                    variant="h3"
                    sx={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontWeight: 'bold',
                        textShadow: '0 0 20px rgba(255, 255, 255, 0.3)',
                        letterSpacing: '0.1em',
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                            '0%, 100%': { opacity: 0.7 },
                            '50%': { opacity: 1 },
                        },
                    }}
                >
                    ⏸️ JUEGO PAUSADO
                </Typography>
            </Backdrop>

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth={isLandscape ? "md" : "xs"}
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
                    }
                }}
                sx={{
                    '& .MuiDialog-paper': {
                        mt: isLandscape ? 5 : 20,
                        maxHeight: isLandscape ? '85vh' : '90vh',
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    Opciones de partida
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: isLandscape ? 2 : 3 }}>
                    {/* Resume Button */}
                    <Box sx={{ mb: isLandscape ? 2 : 3 }}>
                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            color="success"
                            startIcon={<PlayArrowIcon />}
                            onClick={handleClose}
                            sx={{ py: 1.5 }}
                        >
                            Reanudar partida
                        </Button>
                    </Box>

                    <Divider sx={{ my: isLandscape ? 1.5 : 2 }} />

                    {isLandscape ? (
                        // Two-column layout for landscape mobile
                        <Grid container spacing={2}>
                            {/* Left column: Speed and Save */}
                            <Grid item xs={6}>
                                {/* Animation Speed */}
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Velocidad de animación
                                    </Typography>
                                    <Box sx={{ px: 1 }}>
                                        <Slider
                                            value={animationSpeed}
                                            onChange={handleAnimationSpeedChange}
                                            min={0.2}
                                            max={3}
                                            step={0.1}
                                            marks={speedMarks}
                                            valueLabelDisplay="auto"
                                            valueLabelFormat={(value) => `${value.toFixed(1)}x`}
                                            sx={{
                                                '& .MuiSlider-markLabel': {
                                                    fontSize: '0.6rem',
                                                },
                                            }}
                                        />
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                                            {animationSpeed.toFixed(1)}x
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Save Game Button */}
                                <Box>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        startIcon={<SaveIcon />}
                                        onClick={handleSaveGame}
                                        disabled={savingGame}
                                    >
                                        {savingGame ? 'Guardando...' : 'Guardar partida'}
                                    </Button>
                                </Box>
                            </Grid>

                            {/* Right column: Error Report */}
                            <Grid item xs={6}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Enviar informe de error
                                    </Typography>
                                    <TextField
                                        multiline
                                        rows={3}
                                        placeholder="Describe el problema (opcional)"
                                        value={reportMessage}
                                        onChange={(e) => setReportMessage(e.target.value)}
                                        fullWidth
                                        sx={{ mb: 1, flex: 1 }}
                                        size="small"
                                        inputProps={{ maxLength: 5000 }}
                                        helperText={`${reportMessage.length}/5000 caracteres`}
                                    />
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        color="error"
                                        startIcon={<BugReportIcon />}
                                        onClick={handleSendErrorReport}
                                        disabled={sendingReport}
                                    >
                                        {sendingReport ? 'Enviando...' : 'Enviar informe'}
                                    </Button>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}>
                                        Ayúdanos a mejorar
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    ) : (
                        // Single-column layout for portrait
                        <>
                            {/* Animation Speed */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Velocidad de animación
                                </Typography>
                                <Box sx={{ px: 1 }}>
                                    <Slider
                                        value={animationSpeed}
                                        onChange={handleAnimationSpeedChange}
                                        min={0.2}
                                        max={3}
                                        step={0.1}
                                        marks={speedMarks}
                                        valueLabelDisplay="auto"
                                        valueLabelFormat={(value) => `${value.toFixed(1)}x`}
                                        sx={{
                                            '& .MuiSlider-markLabel': {
                                                fontSize: '0.7rem',
                                            },
                                        }}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                                        {animationSpeed.toFixed(1)}x
                                    </Typography>
                                </Box>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {/* Save Game Button */}
                            <Box sx={{ mb: 2 }}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<SaveIcon />}
                                    onClick={handleSaveGame}
                                    disabled={savingGame}
                                >
                                    {savingGame ? 'Guardando...' : 'Guardar partida'}
                                </Button>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {/* Error Report Section */}
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Enviar informe de error
                                </Typography>
                                <TextField
                                    multiline
                                    rows={3}
                                    placeholder="Describe el problema que encontraste (opcional)"
                                    value={reportMessage}
                                    onChange={(e) => setReportMessage(e.target.value)}
                                    fullWidth
                                    sx={{ mb: 2 }}
                                    inputProps={{ maxLength: 5000 }}
                                    helperText={`${reportMessage.length}/5000 caracteres`}
                                />
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    color="error"
                                    startIcon={<BugReportIcon />}
                                    onClick={handleSendErrorReport}
                                    disabled={sendingReport}
                                >
                                    {sendingReport ? 'Enviando...' : 'Enviar informe de error'}
                                </Button>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}>
                                    Ayúdanos a mejorar enviando el estado actual del juego
                                </Typography>
                            </Box>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Snackbar for feedback */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default GameSettingsInGameModal;
